import React from 'react';
import { MapPin, Search, FileText, Edit2, CreditCard, Download, Loader2 } from 'lucide-react';
import { Appointment } from '@/types';
import { Pagination } from '@shared/components/common/Pagination';
import { formatUkDate } from '@/lib/dates';

interface CompanyDashboardBooking {
  appDate: Date | null;
  service: string | undefined;
  venueCity: string | undefined;
  addonsDisplay: string;
  hasCertificate: boolean;
  certificateValidUntil: Date | null;
}

interface CompanyDashboardDashboardTabProps {
  companyAppsCount: number;
  activeEmployees: number;
  paymentPreference: 'invoice' | 'card' | string;
  filteredAppsCount: number;
  bookingsPagination: {
    items: Appointment[];
    currentPage: number;
    totalCount: number;
  };
  isRefreshing: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  onOpenEditBooking: (app: Appointment) => void;
  onViewCertificate: (app: Appointment) => void;
  onDownloadCertificate: (app: Appointment) => void;
  downloadingCertificateAppId: string | null;
  onBookingsPageChange: (page: number) => void;
  onBookingsPageSizeChange: (size: number) => void;
  bookingRowData: (app: Appointment) => CompanyDashboardBooking;
  bookingsPageSize: number;
}

export const CompanyDashboardDashboardTab: React.FC<CompanyDashboardDashboardTabProps> = ({
  companyAppsCount,
  activeEmployees,
  paymentPreference,
  filteredAppsCount,
  bookingsPagination,
  isRefreshing,
  search,
  onSearchChange,
  onOpenEditBooking,
  onViewCertificate,
  onDownloadCertificate,
  downloadingCertificateAppId,
  onBookingsPageChange,
  onBookingsPageSizeChange,
  bookingRowData,
  bookingsPageSize
}) => {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <p className="text-sm text-slate-500 font-medium mb-1">Total Appointments</p>
          <h3 className="text-3xl font-bold text-slate-900">{companyAppsCount}</h3>
        </div>
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <p className="text-sm text-slate-500 font-medium mb-1">Active Employees</p>
          <h3 className="text-3xl font-bold text-slate-900">{activeEmployees}</h3>
        </div>
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <p className="text-sm text-slate-500 font-medium mb-1">Account Status</p>
          <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            {paymentPreference === 'invoice' ? (
              <FileText className="w-5 h-5 text-blue-500" />
            ) : (
              <CreditCard className="w-5 h-5 text-green-500" />
            )}
            <span className="capitalize">{paymentPreference}</span>
          </h3>
          <p className="text-xs text-slate-400 mt-1">Payment Method</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex flex-col md:flex-row justify-between items-center gap-4">
          <div>
            <h3 className="font-bold text-lg text-slate-800">Employee Medical Records</h3>
            <p className="text-xs text-slate-400 font-medium uppercase tracking-tighter mt-0.5">
              Permanent Medical Archive & Certificate Access
            </p>
          </div>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search employee or reference..."
              className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-purple-500 bg-slate-50"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-slate-500 font-medium border-b">
              <tr>
                <th className="p-4">Year</th>
                <th className="p-4">Date & Time</th>
                <th className="p-4">Employee</th>
                <th className="p-4">Service</th>
                <th className="p-4">Location</th>
                <th className="p-4 text-center">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredAppsCount === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-slate-500 italic">
                    No historical medical records found.
                  </td>
                </tr>
              ) : (
                bookingsPagination.items.map((app) => {
                  const {
                    appDate,
                    service,
                    venueCity,
                    addonsDisplay,
                    hasCertificate,
                    certificateValidUntil
                  } = bookingRowData(app);
                  const appYear = appDate ? appDate.getFullYear() : '—';
                  const isExpired = certificateValidUntil && certificateValidUntil < new Date();

                  return (
                    <tr key={app.id} className="hover:bg-slate-50">
                      <td className="p-4 font-bold text-slate-400">{appYear}</td>
                      <td className="p-4">
                        <div className="font-medium text-slate-900">
                          {formatUkDate(appDate, '—')}
                        </div>
                        <div className="text-xs text-slate-500">{app.time}</div>
                      </td>
                      <td className="p-4">
                        <div className="font-medium text-slate-900">{app.customerName}</div>
                        <div className="text-xs text-slate-500">Ref: {app.id}</div>
                      </td>
                      <td className="p-4 text-slate-600">
                        <div className="font-medium">{service}</div>
                        {addonsDisplay && (
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            Add-ons: {addonsDisplay}
                          </div>
                        )}
                        {hasCertificate && (
                          <div
                            className={`text-[10px] font-black uppercase mt-0.5 ${isExpired ? 'text-orange-600' : 'text-green-600'}`}
                          >
                            {isExpired ? 'Validity Expired' : 'Currently Valid'}
                          </div>
                        )}
                      </td>
                      <td className="p-4 text-slate-600 flex items-center gap-1 mt-3">
                        <MapPin className="w-3 h-3 text-slate-400" /> {venueCity}
                      </td>
                      <td className="p-4 text-center">
                        <span
                          className={`px-2 py-1 rounded text-xs font-bold uppercase ${
                            app.status === 'completed'
                              ? 'bg-green-100 text-green-700'
                              : app.status === 'booked'
                                ? 'bg-blue-100 text-blue-700'
                                : app.status === 'noshow'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {app.status === 'noshow' ? 'No Show' : app.status}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => onOpenEditBooking(app)}
                            className="text-slate-600 hover:text-purple-700 text-sm font-bold flex items-center gap-1 bg-slate-50 px-3 py-1 rounded-lg border border-slate-200 transition-all hover:shadow-sm"
                          >
                            <Edit2 className="w-4 h-4" /> Amend
                          </button>
                          {hasCertificate && (
                            <>
                              <button
                                onClick={() => onViewCertificate(app)}
                                className="text-purple-600 hover:text-purple-800 text-sm font-bold flex items-center gap-1 bg-purple-50 px-3 py-1 rounded-lg border border-purple-100 transition-all hover:shadow-sm"
                              >
                                <FileText className="w-4 h-4" /> View
                              </button>
                              <button
                                onClick={() => onDownloadCertificate(app)}
                                disabled={downloadingCertificateAppId !== null}
                                className="text-white bg-purple-600 hover:bg-purple-700 text-sm font-bold flex items-center gap-1 px-3 py-1 rounded-lg border border-purple-600 transition-all hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {downloadingCertificateAppId === app.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Download className="w-4 h-4" />
                                )}{' '}
                                Download
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="p-6 pt-4">
          <Pagination
            page={bookingsPagination.currentPage}
            pageSize={bookingsPageSize}
            totalCount={bookingsPagination.totalCount}
            onPageChange={onBookingsPageChange}
            onPageSizeChange={(size) => {
              onBookingsPageSizeChange(size);
            }}
            isLoading={isRefreshing}
          />
        </div>
      </div>
    </>
  );
};
